import { extractSeoRoutePath, isPrivateSeoRoute } from './seo-indexability';
import { shouldSkipPublicMeasurement } from './measurement-hosts';

/**
 * Google Tag Manager & GA4 Integration
 * Consent Mode V2 compliant implementation
 * 
 * SETUP REQUIRED:
 * 1. Create GTM container at https://tagmanager.google.com
 * 2. Create GA4 property at https://analytics.google.com
 * 3. Add GTM_ID and GA4_MEASUREMENT_ID to environment
 */

// GTM Container ID - Replace with actual ID or use env variable
export const GTM_ID = import.meta.env.VITE_GTM_ID || '';
export const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || '';

export type ConsentState = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

function isLikelyBotUserAgent(userAgent: string): boolean {
  return /bot|crawler|spider|slurp|bingpreview|mediapartners-google|adsbot|google-inspectiontool|headless|lighthouse|pagespeed|curl|wget|python-requests|httpclient/i.test(userAgent);
}

export function shouldSkipAnalyticsTracking(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;
  if (shouldSkipPublicMeasurement(window.location.hostname)) return true;
  if (isPrivateSeoRoute(extractSeoRoutePath(window.location.pathname))) return true;
  return isLikelyBotUserAgent(navigator.userAgent || '');
}

export function shouldInjectTrackingScripts(): boolean {
  return !shouldSkipAnalyticsTracking();
}

export const COOKIE_CONSENT_KEY = 'bpoContact_cookie_consent_v2';

/**
 * The visitor's explicit saved choice, or null when there is none
 * (absent, corrupt or storage unavailable).
 */
export function readSavedConsent(): ConsentState | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
  } catch (err) {
    console.warn('[consent] storage unavailable, treating as no choice', err);
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (v && typeof v === 'object' && typeof v.analytics === 'boolean' && typeof v.marketing === 'boolean') {
      return { necessary: true, analytics: v.analytics, marketing: v.marketing };
    }
  } catch (err) {
    console.warn('[consent] corrupt saved choice, treating as no choice', err);
  }
  return null;
}

/**
 * The single consent decision used by every producer: the saved explicit
 * choice wins; without a choice nothing beyond strictly necessary is granted.
 */
export function resolveConsent(): ConsentState {
  return readSavedConsent() ?? { necessary: true, analytics: false, marketing: false };
}

/**
 * Initialize GTM dataLayer with Consent Mode V2 defaults
 * IMPORTANT: Must be called BEFORE GTM script loads. Runs once per page; the
 * baseline comes from resolveConsent(), never from remote config.
 */
export function initializeConsentDefaults(_legacyDefaultDeny?: boolean) {
  window.dataLayer = window.dataLayer || [];
  if ((window as any).__bpoConsentDefaultSet) return;
  (window as any).__bpoConsentDefaultSet = true;

  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }

  const decision = resolveConsent();
  const analyticsStorage = decision.analytics ? 'granted' : 'denied';
  const adStorage = decision.marketing ? 'granted' : 'denied';

  // Set default consent baseline
  gtag('consent', 'default', {
    'ad_storage': adStorage,
    'ad_user_data': adStorage,
    'ad_personalization': adStorage,
    'analytics_storage': analyticsStorage,
    'functionality_storage': 'granted', // Required for site to work
    'personalization_storage': adStorage,
    'security_storage': 'granted', // Required for security features
    'wait_for_update': 500,
  });
  
  // Enable URL passthrough for better measurement without cookies
  gtag('set', 'url_passthrough', true);
  
  // Enable ads data redaction when consent denied
  gtag('set', 'ads_data_redaction', true);
}

/**
 * Update consent based on user preferences
 * Called when user interacts with cookie banner
 */
export function updateConsent(consent: ConsentState) {
  window.dataLayer = window.dataLayer || [];
  
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  
  gtag('consent', 'update', {
    'ad_storage': consent.marketing ? 'granted' : 'denied',
    'ad_user_data': consent.marketing ? 'granted' : 'denied',
    'ad_personalization': consent.marketing ? 'granted' : 'denied',
    'analytics_storage': consent.analytics ? 'granted' : 'denied',
    'personalization_storage': consent.marketing ? 'granted' : 'denied',
  });
  
  // Push consent event for GTM triggers
  window.dataLayer.push({
    event: 'consent_update',
    consent_analytics: consent.analytics,
    consent_marketing: consent.marketing,
  });
}

/**
 * Push event to dataLayer
 */
export function pushToDataLayer(event: string, data?: Record<string, any>) {
  if (shouldSkipAnalyticsTracking()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...data,
  });
}

/**
 * Track page view (for SPAs)
 */
export function trackPageView(pagePath: string, pageTitle?: string) {
  pushToDataLayer('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: window.location.href,
  });
}

/**
 * Track standard GA4 events
 */
export const GA4Events = {
  // Lead Generation
  generateLead: (data?: { currency?: string; value?: number; form_name?: string }) => {
    pushToDataLayer('generate_lead', {
      currency: data?.currency || 'USD',
      value: data?.value || 0,
      form_name: data?.form_name,
    });
  },
  
  // Engagement
  viewItem: (item: { item_id: string; item_name: string; item_category?: string }) => {
    pushToDataLayer('view_item', {
      items: [item],
    });
  },
  
  // CTA Clicks
  ctaClick: (ctaName: string, ctaLocation: string) => {
    pushToDataLayer('cta_click', {
      cta_name: ctaName,
      cta_location: ctaLocation,
    });
  },
  
  // Form Interactions
  formStart: (formName: string) => {
    pushToDataLayer('form_start', { form_name: formName });
  },
  
  formSubmit: (formName: string, success: boolean) => {
    pushToDataLayer('form_submit', { 
      form_name: formName,
      form_submit_success: success,
    });
  },
  
  // Contact Events
  phoneClick: (phoneNumber: string) => {
    pushToDataLayer('phone_click', { phone_number: phoneNumber });
  },
  
  emailClick: (emailAddress: string) => {
    pushToDataLayer('email_click', { email_address: emailAddress });
  },
  
  whatsappClick: () => {
    pushToDataLayer('whatsapp_click');
  },
  
  // Service Interactions
  serviceView: (serviceName: string, serviceCategory: string) => {
    pushToDataLayer('service_view', {
      service_name: serviceName,
      service_category: serviceCategory,
    });
  },
  
  quoteRequest: (serviceName?: string, estimatedValue?: number) => {
    pushToDataLayer('quote_request', {
      service_name: serviceName,
      estimated_value: estimatedValue,
    });
  },
  
  pilotRequest: () => {
    pushToDataLayer('pilot_request');
  },
  
  // Downloads
  fileDownload: (fileName: string, fileType: string) => {
    pushToDataLayer('file_download', {
      file_name: fileName,
      file_extension: fileType,
    });
  },
  
  // Scroll Tracking (if needed beyond GTM built-in)
  scrollDepth: (percent: number) => {
    pushToDataLayer('scroll', { percent_scrolled: percent });
  },

  // ============ ORDER WIDGET FUNNEL TRACKING ============
  
  /** Fired when the order widget becomes visible to the user */
  widgetView: (data: { service: string; language: string; country?: string }) => {
    pushToDataLayer('widget_view', {
      widget_service: data.service,
      widget_language: data.language,
      widget_country: data.country || 'unknown',
    });
  },

  /** Fired when user changes a widget configuration option */
  widgetConfigure: (data: {
    service: string;
    field: string;
    value: string;
    language: string;
    country?: string;
  }) => {
    pushToDataLayer('widget_configure', {
      widget_service: data.service,
      widget_field: data.field,
      widget_value: data.value,
      widget_language: data.language,
      widget_country: data.country || 'unknown',
    });
  },

  /** Fired when user clicks the "Order" / CTA button on the widget */
  widgetOrderClick: (data: {
    service: string;
    channels: string[];
    languages: string[];
    coverage: string;
    agentCount: number;
    hourlyRate: number;
    language: string;
    country?: string;
  }) => {
    pushToDataLayer('widget_order_click', {
      widget_service: data.service,
      widget_channels: data.channels.join(','),
      widget_languages: data.languages.join(','),
      widget_coverage: data.coverage,
      widget_agent_count: data.agentCount,
      widget_hourly_rate: data.hourlyRate,
      widget_ui_language: data.language,
      widget_country: data.country || 'unknown',
      currency: 'EUR',
      value: data.hourlyRate * data.agentCount,
    });
  },

  /** Fired when the order form is successfully submitted */
  widgetOrderSubmit: (data: {
    service: string;
    orderNumber?: string;
    totalValue: number;
    language: string;
    country?: string;
    isNewUser: boolean;
  }) => {
    pushToDataLayer('widget_order_submit', {
      widget_service: data.service,
      widget_order_number: data.orderNumber || '',
      widget_total_value: data.totalValue,
      widget_ui_language: data.language,
      widget_country: data.country || 'unknown',
      widget_is_new_user: data.isNewUser,
      currency: 'EUR',
      value: data.totalValue,
    });
    // Also fire GA4 purchase-like event for conversion tracking
    pushToDataLayer('generate_lead', {
      currency: 'EUR',
      value: data.totalValue,
      form_name: 'instant_order_widget',
    });
  },
};

// Declare dataLayer type for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}
